import { useId, useState } from 'react'
import type { JSX } from 'react'
import type { ReactElement, ReactNode } from 'react'

import { ResponsiveContainer } from 'recharts'

import { Text, VisuallyHidden } from '@buffer-mono/popcorn'

import { ChartDataTable } from './ChartDataTable'

import styles from './ChartFrame.module.css'

interface ChartFrameProps {
  /** Accessible name for the chart region and the data-table caption. */
  ariaLabel: string
  /** Month-only date labels for monthly-bucketed data (data table). */
  monthly?: boolean
  /** Overlay shown when there's no data; the axes/grid stay visible behind it. */
  emptyState?: ReactNode
  height?: number
  /** The screen-reader data-table alternative revealed by the disclosure.
   *  Defaults to the time-series {@link ChartDataTable}; a chart whose data
   *  isn't per-timestamp (e.g. a per-channel diverging chart) supplies its own. */
  dataTable?: ReactNode
  /** The Recharts chart element (a single `<LineChart>`/`<BarChart>`). */
  children: ReactElement
}

/**
 * Shared chrome for the Cartesian charts: a labelled `<figure>`, the
 * screen-reader {@link ChartDataTable} alternative, the responsive sizing
 * container, and the empty-state overlay. The chart type (lines vs columns) is
 * the `children` — this frame is identical for both.
 */
export function ChartFrame({
  ariaLabel,
  monthly = false,
  emptyState,
  height = 250,
  dataTable,
  children,
}: ChartFrameProps): JSX.Element {
  const [showDataTable, setShowDataTable] = useState(false)
  const tableId = useId()

  return (
    <figure className={styles.chartRoot} aria-label={ariaLabel}>
      {/* Screen-reader / keyboard affordance to expose the data-table
          alternative. Visually hidden for now (no visible design yet) but
          focusable, so assistive-tech users can Tab to it and reveal the
          per-point data, which the chart's SVG can't announce on its own. */}
      <VisuallyHidden
        as="button"
        type="button"
        className={styles.dataTableToggle}
        aria-expanded={showDataTable}
        aria-controls={tableId}
        onClick={(): void => setShowDataTable((shown) => !shown)}
      >
        {showDataTable ? 'Hide data table' : 'Show data table'}
      </VisuallyHidden>

      {/* Data alternative — reads the same registry as the chart, so it can't
          drift from what's drawn. Rendered only while expanded. Defaults to the
          time-series table; a chart can supply its own shape via `dataTable`. */}
      <VisuallyHidden as="div" id={tableId}>
        {showDataTable &&
          (dataTable ?? (
            <ChartDataTable caption={ariaLabel} monthly={monthly} />
          ))}
      </VisuallyHidden>

      <ResponsiveContainer width="100%" height={height}>
        {children}
      </ResponsiveContainer>

      {emptyState && (
        <div className={styles.chartEmptyOverlay}>
          {typeof emptyState === 'string' ? (
            <Text size="sm" color="subtle">
              {emptyState}
            </Text>
          ) : (
            emptyState
          )}
        </div>
      )}
    </figure>
  )
}
